import { BookOpen, ChevronDown, Film, Gavel, Globe, Languages, Scale } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { TalkToUs } from "@/components/TalkToUs";
import { useLocalePath } from "@/lib/locale";

/*
 * Apple.com-replica marketing system.
 * Rules: no cards, no icons, no illustrations. Photography + typography only.
 * Client image package (self-hosted, /public/images/home/) — every editorial
 * block overlays type directly on the photograph; text color is chosen per
 * image (light image -> near-black type, dark image -> white type).
 */

export const IMAGES = {
  hero: "/images/home/hero-banner.webp", // light — black type (WS10: WebP optimizado, LCP)
  brand: "/images/home/your-auction-your-brand.webp", // light — black type
  individual: "/images/home/individual-seller.webp", // dark — white type
  breeder: "/images/home/breeder.webp", // light — black type
  professional: "/images/home/professional-organizer.webp", // dark, wide — white type
  how: "/images/home/pre-pricing-official-wide.jpg", // official grey jumper on white — divider before pricing
  beforeIncluded: "/images/home/before-everything-included.jpeg", // editorial band
  included: "/images/home/everything-included.jpeg", // light — black type
  final: "/images/home/before-footer.jpg", // mid/dark garden — white type + scrim
  about: "/images/home/before-everything-included.jpeg", // on-brand equestrian editorial
};

/* ---------- Apple type scale ---------- */
export const H_SECTION =
  "text-4xl sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.015em] leading-[1.07]";
export const H_TILE = "text-3xl lg:text-[40px] font-semibold tracking-[-0.01em] leading-[1.1]";
export const SUBLINE = "text-lg lg:text-[24px] font-normal leading-snug";

/* ---------- Apple pill buttons ---------- */
export const PrimaryCta = ({ to, children, testid, small = false }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`inline-flex items-center justify-center rounded-full bg-[#0A0A0A] text-white transition-colors duration-300 hover:bg-[#2b2b2b] ${
      small ? "text-sm px-[16px] py-[8px]" : "text-[17px] px-[22px] py-[11px]"
    }`}
  >
    {children}
  </Link>
);

export const SecondaryCta = ({ to, children, testid, small = false, onDark = false }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`inline-flex items-center justify-center rounded-full transition-colors duration-300 ${
      onDark
        ? "bg-transparent text-white border border-white/60 hover:border-white"
        : "bg-white text-[#0A0A0A] border border-[#0A0A0A]/30 hover:border-[#0A0A0A]"
    } ${small ? "text-sm px-[16px] py-[7px]" : "text-[17px] px-[22px] py-[10px]"}`}
  >
    {children}
  </Link>
);

/* ---------- Hero: light photograph fills the stage, black type overlaid ---------- */
export const HeroSection = () => {
  const { t } = useTranslation();
  return (
    <section
      className="relative min-h-[620px] h-[92vh] max-h-[900px] overflow-hidden bg-[#f4f4f4]"
      data-testid="home-hero"
    >
      <img
        src={IMAGES.hero}
        alt=""
        data-testid="hero-image"
        width="1536"
        height="1024"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover object-[center_72%]"
        loading="eager"
      />
      <div className="relative h-full max-w-[980px] mx-auto px-6 flex flex-col items-center text-center pt-12 lg:pt-16">
        <h1
          data-testid="hero-title"
          className="eq-fade-up text-4xl sm:text-5xl lg:text-[56px] font-semibold tracking-[-0.015em] leading-[1.06] text-[#1d1d1f]"
        >
          {t("mk.title1")}
          <br />
          {t("mk.title2")}
        </h1>
        <p className={`eq-fade-up eq-delay-1 mt-4 text-base lg:text-[19px] font-normal leading-snug text-[#4b4b50] max-w-xl`}>{t("mk.subtitle")}</p>
        <div className="eq-fade-up eq-delay-2 mt-6 flex flex-wrap items-center justify-center gap-3">
          <PrimaryCta small to="/create" testid="hero-cta-create">{t("mk.cta")}</PrimaryCta>
          <SecondaryCta small to="/auctions" testid="hero-cta-browse">{t("mk.ctaSecondary")}</SecondaryCta>
        </div>
      </div>
    </section>
  );
};

/* ---------- Your Auction. Your Brand. — photo IS the section, type overlaid ---------- */
export const BrandSection = () => {
  const { t } = useTranslation();
  return (
    <section
      className="relative min-h-[560px] h-[86vh] max-h-[820px] overflow-hidden bg-[#ececec]"
      data-testid="brand-section"
    >
      <img
        src={IMAGES.brand}
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-[center_22%]"
        loading="lazy"
      />
      <div className="relative h-full max-w-[980px] mx-auto px-6 flex flex-col items-center text-center pt-12 lg:pt-16">
        <h2 className={`${H_SECTION} text-[#1d1d1f]`}>{t("mk.brandTitle")}</h2>
        <p className={`mt-4 ${SUBLINE} text-[#4b4b50] max-w-2xl`}>{t("mk.brandSub")}</p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-4">
          <PrimaryCta small to="/create" testid="brand-cta-start">{t("mk.startNow")}</PrimaryCta>
          <SecondaryCta small to="/how-it-works" testid="brand-cta-learn">{t("mk.learnMore")}</SecondaryCta>
        </div>
      </div>
    </section>
  );
};

/* ---------- For every organizer: 2 squares + 1 wide module, 22px radius ---------- */
const TILE_RADIUS = "rounded-[22px]";

const Tile = ({ index, img, dark, wide = false, objectPos = "object-center" }) => {
  const { t } = useTranslation();
  return (
    <article
      className={`relative overflow-hidden ${TILE_RADIUS} ${wide ? "md:col-span-2 h-[420px] lg:h-[520px] bg-[#050505]" : "h-[520px] lg:h-[580px]"} ${dark ? "bg-[#050505]" : "bg-[#f2f2f2]"}`}
      data-testid={`organizer-type-${index}`}
    >
      <img src={img} alt="" className={`absolute inset-0 w-full h-full object-cover ${objectPos}`} loading="lazy" />
      <div className="relative flex flex-col items-center text-center gap-3 pt-12 lg:pt-14 px-8">
        <h3 className={`${H_TILE} ${dark ? "text-white" : "text-[#1d1d1f]"}`}>{t(`mk.type${index}Title`)}</h3>
        <p className={`text-base lg:text-[19px] leading-snug max-w-md ${dark ? "text-white/85" : "text-[#4b4b50]"}`}>
          {t(`mk.type${index}Desc`)}
        </p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-3">
          <PrimaryCta small to="/create" testid={`tile-${index}-start`}>{t("mk.startNow")}</PrimaryCta>
          <SecondaryCta small onDark={dark} to="/how-it-works" testid={`tile-${index}-learn`}>{t("mk.learnMore")}</SecondaryCta>
        </div>
      </div>
    </article>
  );
};

export const OrganizerGrid = () => (
  <section className="bg-white py-4" data-testid="organizer-types-section">
    <div className="max-w-[1440px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
      {/* top row: Individual Seller + Breeder */}
      <Tile index={1} img={IMAGES.individual} dark objectPos="object-[center_65%]" />
      <Tile index={2} img={IMAGES.breeder} dark={false} objectPos="object-[center_70%]" />
      {/* bottom row: Professional Organizer, wide */}
      <Tile index={4} img={IMAGES.professional} dark wide objectPos="object-[center_75%]" />
    </div>
  </section>
);

/* ---------- How it works: type + plain text steps + divider photo before pricing ---------- */
export const HowItWorksSection = ({ showCta = true, HeadingTag = "h2" }) => {
  const { t } = useTranslation();
  const steps = [1, 2, 3, 4].map((n) => ({
    n: String(n).padStart(2, "0"),
    title: t(`mk.step${n}Title`),
    desc: t(`mk.step${n}Desc`),
  }));
  return (
    <section id="how-it-works" className="bg-white pt-20 lg:pt-28" data-testid="how-it-works-section">
      <div className="max-w-[980px] mx-auto px-6 text-center flex flex-col items-center gap-4">
        <HeadingTag className={`${H_SECTION} text-[#1d1d1f]`}>{t("mk.howTitle")}</HeadingTag>
        <p className={`${SUBLINE} text-[#6e6e73] max-w-2xl`}>{t("mk.howSub")}</p>
        {showCta && (
          <div className="mt-2">
            <PrimaryCta small to="/create" testid="how-cta-create">{t("mk.cta")}</PrimaryCta>
          </div>
        )}
      </div>
      <div className="max-w-[980px] mx-auto px-6 mt-14 lg:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 text-left">
        {steps.map((s) => (
          <article key={s.n} className="flex flex-col gap-2" data-testid={`how-step-${s.n}`}>
            <span className="text-sm font-medium tabular-nums text-[#1D4D3B]">{s.n}</span>
            <h3 className="text-[17px] font-semibold text-[#1d1d1f]">{s.title}</h3>
            <p className="text-sm text-[#6e6e73] leading-relaxed">{s.desc}</p>
          </article>
        ))}
      </div>
      {/* full horse visible, editorial air around it — bg matches the image's own gradient */}
      {/* official pre-pricing image: full horse, white stage, generous air */}
      <div className="mt-14 lg:mt-20 bg-white">
        <img
          src={IMAGES.how}
          alt=""
          className="w-full h-[320px] lg:h-[520px] object-cover object-center"
          loading="lazy"
        />
      </div>
    </section>
  );
};

/* ---------- Editorial band before "Everything included" — pure photography ---------- */
export const EditorialBreak = () => (
  <section className="bg-[#0A0A0A]" data-testid="editorial-break-section">
    <img
      src={IMAGES.beforeIncluded}
      alt=""
      className="w-full h-[380px] lg:h-[640px] object-cover object-[center_35%]"
      loading="lazy"
    />
  </section>
);

/* ---------- Everything included: light photo, black type over soft white scrim ---------- */
const INCLUDED_ICONS = [Globe, Languages, Scale, Film, BookOpen, Gavel];

export const IncludedSection = () => {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden bg-[#eef1f4]" data-testid="included-section">
      <img src={IMAGES.included} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      {/* stronger scrim (client pass 4): text leads decisively, photo stays present */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-white/[0.96] via-white/[0.87] to-white/[0.94]"
        aria-hidden="true"
      />
      <div className="relative max-w-[980px] mx-auto px-6 py-24 lg:py-32">
        <div className="text-center flex flex-col items-center gap-4">
          <h2 className={`${H_SECTION} text-[#1d1d1f]`}>{t("mk.includedTitle")}</h2>
          <p className={`${SUBLINE} text-[#4b4b50]`}>{t("mk.includedSub")}</p>
        </div>
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-12 text-left">
          {[1, 2, 3, 4, 5, 6].map((n) => {
            const Icon = INCLUDED_ICONS[n - 1];
            return (
              <div key={n} className="flex flex-col gap-3" data-testid={`included-item-${n}`}>
                <Icon className="w-6 h-6 text-[#1d1d1f]" strokeWidth={1.5} aria-hidden="true" />
                <h3 className="text-[17px] font-semibold text-[#1d1d1f]">{t(`mk.included${n}Title`)}</h3>
                <p className="text-sm text-[#3f3f44] leading-relaxed">{t(`mk.included${n}Desc`)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ---------- Brand statement: pure typography.
   (Fase 13/WS12: sin testimonios ficticios — el antiguo quote atribuido a un
   organizador de seed demo se sustituyó por una declaración de marca neutra.) ---------- */
export const QuoteSection = () => {
  const { t } = useTranslation();
  return (
    <section className="bg-[#f5f5f7]" data-testid="brand-statement-section">
      <div className="max-w-[780px] mx-auto px-6 py-24 lg:py-32 text-center">
        <p className="text-2xl sm:text-3xl lg:text-[40px] font-semibold tracking-[-0.01em] text-[#1d1d1f] leading-[1.2]">
          {t("mk.brandQuote")}
        </p>
      </div>
    </section>
  );
};

/* ---------- FAQ as a sales tool: 19 questions, 8 visible, Apple-style accordions.
   Content doubles as FAQPage Schema.org JSON-LD for SEO. ---------- */
const FAQ_TOTAL = 21;
const FAQ_VISIBLE = 8;

export const FaqSection = () => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const count = expanded ? FAQ_TOTAL : FAQ_VISIBLE;

  const jsonLd = useMemo(
    () =>
      JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [...Array(FAQ_TOTAL)].map((_, i) => ({
          "@type": "Question",
          name: t(`faq.q${i + 1}`),
          acceptedAnswer: { "@type": "Answer", text: t(`faq.a${i + 1}`) },
        })),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.resolvedLanguage]
  );

  return (
    <section className="bg-white" data-testid="faq-section">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />
      <div className="max-w-[720px] mx-auto px-6 py-20 lg:py-28">
        <h2 className={`${H_SECTION} text-[#1d1d1f] text-center`}>{t("faq.title")}</h2>
        <Accordion type="single" collapsible className="mt-12">
          {[...Array(count)].map((_, i) => {
            const n = i + 1;
            return (
              <AccordionItem key={n} value={`q${n}`} className="border-[#d2d2d7]">
                <AccordionTrigger
                  data-testid={`faq-trigger-${n}`}
                  className="text-left text-[17px] font-semibold text-[#1d1d1f] py-5 hover:no-underline"
                >
                  {t(`faq.q${n}`)}
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-[#6e6e73] leading-relaxed pb-6">
                  {t(`faq.a${n}`)}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
        {!expanded && (
          <div className="mt-10 text-center">
            <button
              type="button"
              data-testid="faq-show-more"
              onClick={() => setExpanded(true)}
              className="inline-flex items-center gap-2 rounded-full border border-[#0A0A0A]/25 bg-white px-[22px] py-[10px] text-[15px] font-medium text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors duration-300"
            >
              {t("faq.showMore")}
              <ChevronDown className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}
        <TalkToUs testid="faq-talk-to-us" />
      </div>
    </section>
  );
};

/* ---------- Real example vignettes — "built for someone exactly like me" ---------- */
const VIGNETTES = [
  { key: 1, img: IMAGES.breeder, pos: "object-[center_70%]" },
  { key: 2, img: IMAGES.individual, pos: "object-[center_65%]" },
  { key: 3, img: IMAGES.professional, pos: "object-[center_75%]" },
  { key: 4, img: IMAGES.beforeIncluded, pos: "object-[center_35%]" },
];

export const VignettesSection = () => {
  const { t } = useTranslation();
  return (
    <section className="bg-[#f5f5f7] py-20 lg:py-28" data-testid="vignettes-section">
      <div className="max-w-[980px] mx-auto px-6 text-center flex flex-col items-center gap-4">
        <h2 className={`${H_SECTION} text-[#1d1d1f]`}>{t("hiw.vignettesTitle")}</h2>
        <p className={`${SUBLINE} text-[#6e6e73] max-w-2xl`}>{t("hiw.vignettesSub")}</p>
      </div>
      <div className="max-w-[1200px] mx-auto px-6 mt-14 lg:mt-20 flex flex-col gap-10 lg:gap-14">
        {VIGNETTES.map((v, i) => (
          <article
            key={v.key}
            className={`grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-14 items-center ${i % 2 === 1 ? "md:[direction:rtl]" : ""}`}
            data-testid={`vignette-${v.key}`}
          >
            <div className="relative overflow-hidden rounded-[22px] h-[300px] lg:h-[400px] [direction:ltr]">
              <img src={v.img} alt="" className={`absolute inset-0 w-full h-full object-cover ${v.pos}`} loading="lazy" />
            </div>
            <div className="flex flex-col gap-3 text-left [direction:ltr]">
              <span className="text-sm font-medium text-[#1D4D3B]">{t(`hiw.v${v.key}Kicker`)}</span>
              <h3 className={`${H_TILE} text-[#1d1d1f]`}>{t(`hiw.v${v.key}Title`)}</h3>
              <p className="text-base lg:text-[19px] leading-relaxed text-[#4b4b50]">{t(`hiw.v${v.key}Text`)}</p>
              <div className="mt-2">
                <PrimaryCta small to="/create" testid={`vignette-${v.key}-cta`}>{t("mk.cta")}</PrimaryCta>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

/* ---------- Final CTA: garden photograph, white type over a soft dark scrim ---------- */
export const FinalCtaSection = () => {
  const { t } = useTranslation();
  const lp = useLocalePath();
  return (
    <section className="relative overflow-hidden bg-[#0A0A0A]" data-testid="final-cta-section">
      <img src={IMAGES.final} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/35 to-black/55" aria-hidden="true" />
      <div className="relative max-w-[980px] mx-auto px-6 py-28 lg:py-40 text-center flex flex-col items-center gap-5">
        <h2 className={`${H_SECTION} text-white max-w-3xl`}>{t("mk.finalTitle")}</h2>
        <p className={`${SUBLINE} text-white/85 max-w-xl`}>{t("mk.finalSubtitle")}</p>
        <div className="mt-4 flex flex-wrap items-center justify-center gap-4">
          <SecondaryCta to="/create" testid="final-cta-button">{t("mk.finalCta")}</SecondaryCta>
        </div>
        {/* WS4: enlaces internos a las páginas corporativas por audiencia */}
        <nav aria-label={t("ft.eyebrow")} className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
          <Link to={lp("/for-organisers")} data-testid="home-link-for-organisers" className="text-sm text-white/75 hover:text-white hover:underline">
            {t("nav.forOrganisers")}
          </Link>
          <Link to={lp("/for-buyers")} data-testid="home-link-for-buyers" className="text-sm text-white/75 hover:text-white hover:underline">
            {t("nav.forBuyers")}
          </Link>
          <Link to={lp("/features")} data-testid="home-link-features" className="text-sm text-white/75 hover:text-white hover:underline">
            {t("nav.features")}
          </Link>
        </nav>
      </div>
    </section>
  );
};
